import {
  IssueList,
  Labelled,
  NumberInput,
  Worked,
  controlClass,
  smallButtonClass,
} from '../points/controls';
import {
  nextInputId,
  weightShares,
  type SalaryInput,
  type SalarySource,
} from './salaryDefinition';
import { salaryHasError, salaryIssuesFor, type SalaryValidation } from './salaryValidate';
import { SALARY_SOURCE_CHOICES } from './salaryWording';

/**
 * §4.2's `inputs` — what a price is built from, and how much each thing counts.
 *
 * Three decisions worth not undoing:
 *
 * - **The short name is generated and never shown.** §4.2 requires an id, unique in the document,
 *   and it is an audit-trail key rather than anything an operator has a view about. A form asking a
 *   card room manager to invent `boardPoints` is asking the wrong person a question, so
 *   `nextInputId` derives one from the source and the field is absent.
 * - **The weight box is paired with the share it works out to.** Weights are normalised to sum to
 *   1.0 at validation, so `60` on its own says nothing — `60` beside `→ 60% of the price` is what
 *   the operator was actually setting. *Make these add to 100* rewrites the weights to their own
 *   shares, so the two readings agree.
 * - **`rating` is not offered.** It is in the schema's enum on purpose, so a document naming it can
 *   be refused with "reserved for a later version" rather than an enum mismatch — but there is no
 *   reason to put a control on the screen whose only outcome is that refusal.
 */
export function SalaryInputsEditor({
  inputs,
  onChange,
  validation,
  readOnly = false,
}: {
  inputs: readonly SalaryInput[];
  onChange: (next: SalaryInput[]) => void;
  validation: SalaryValidation;
  readOnly?: boolean;
}) {
  const shares = weightShares(inputs);
  const total = inputs.reduce((sum, input) => sum + Math.max(input.weight ?? 0, 0), 0);
  const alreadyBalanced = inputs.every(
    (input, i) => Math.abs((input.weight ?? 0) - (shares[i] ?? 0)) < 0.005,
  );

  const set = (index: number, patch: Partial<SalaryInput>) =>
    onChange(inputs.map((input, i) => (i === index ? { ...input, ...patch } : input)));

  const addable = SALARY_SOURCE_CHOICES.map((c) => c.value).find(
    (source) => !inputs.some((input) => input.source === source),
  );

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {inputs.map((input, index) => {
          const choice = SALARY_SOURCE_CHOICES.find((c) => c.value === input.source);
          const invalid = salaryHasError(validation, 'inputs', index);

          return (
            <div
              key={input.id || index}
              className={`border p-3 ${invalid ? 'border-bad/50' : 'border-line'} bg-surface`}
            >
              <div className="flex flex-wrap items-end gap-3">
                <Labelled label="Priced on" className="flex-1 basis-56">
                  <select
                    value={input.source}
                    disabled={readOnly}
                    aria-label={`What input ${index + 1} reads`}
                    onChange={(e) => {
                      const source = e.target.value as SalarySource;
                      set(index, {
                        source,
                        // The id follows the source, so the audit trail stays readable and stays
                        // unique. Only when the operator has not been shown it to change.
                        id: nextInputId(
                          source,
                          inputs.filter((_, i) => i !== index),
                        ),
                      });
                    }}
                    className={`w-full ${controlClass}`}
                  >
                    {SALARY_SOURCE_CHOICES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                    {/* A stored document may name something this build does not offer — the
                        reserved source, or a value from a later schema. Shown so the screen
                        reports what is stored rather than silently changing it. */}
                    {choice ? null : <option value={input.source}>{input.source}</option>}
                  </select>
                </Labelled>

                <Labelled label="Weight" className="w-24 shrink-0">
                  <NumberInput
                    value={input.weight}
                    onChange={(weight) => set(index, { weight })}
                    ariaLabel={`Weight for ${choice?.label ?? input.source}`}
                    min={0}
                    step={0.05}
                    disabled={readOnly}
                    invalid={invalid}
                    className="w-full text-right"
                  />
                </Labelled>

                <div className="w-28 shrink-0 pb-1.5">
                  <span className="num text-sm font-semibold text-ink-950">
                    {total > 0 ? `${Math.round(shares[index] ?? 0)}%` : '—'}
                  </span>
                  <span className="ml-1 text-[11px] text-ink-800/50">of the price</span>
                </div>

                {!readOnly && inputs.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => onChange(inputs.filter((_, i) => i !== index))}
                    className={`${smallButtonClass} mb-1`}
                  >
                    Remove
                  </button>
                ) : null}
              </div>

              {choice ? (
                <p className="mt-2 text-[11.5px] leading-relaxed text-ink-800/55">
                  {choice.detail}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      {!readOnly ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!addable || inputs.length >= 6}
            title={
              addable
                ? undefined
                : 'Every figure a model can read is already in the list.'
            }
            onClick={() => {
              if (!addable) return;
              onChange([
                ...inputs,
                { id: nextInputId(addable, inputs), source: addable, weight: 0.1 },
              ]);
            }}
            className={smallButtonClass}
          >
            Add something else
          </button>
          <button
            type="button"
            disabled={total <= 0 || alreadyBalanced}
            onClick={() =>
              onChange(
                inputs.map((input, i) => ({
                  ...input,
                  weight: Number((shares[i] ?? 0).toFixed(2)),
                })),
              )
            }
            className={smallButtonClass}
          >
            Make these add to 100
          </button>
        </div>
      ) : null}

      {/* §4.3's argument, stated once where the decision is made rather than left in a spec. */}
      <Worked>
        Cashing often is the signal that holds up. Over the reference series, how much somebody won
        last time told you almost nothing about how they would do next time, while how often they
        cashed told you a great deal — so a model built mostly on money prices last season’s luck.
      </Worked>

      <IssueList issues={salaryIssuesFor(validation, 'inputs')} />
    </div>
  );
}
